// "Arrive at" dispatch (G-59, P32): pure time and reservation helpers for the send and Reinforce
// screens. Times are epoch milliseconds; the input field is local time.
import type {
  FleetOrderListResponse,
  ReinforceMission,
  ReinforceOption,
  ShipCounts,
  ShipType,
} from '@stars-and-ships/api-types';

const MINUTE = 60_000;
const HOUR = 3_600_000;

export const roundUpToMinute = (ms: number) => Math.ceil(ms / MINUTE) * MINUTE;
export const earliestArrival = (nowMs: number, totalS: number) =>
  roundUpToMinute(nowMs + totalS * 1000);
export const departureFor = (arriveAtMs: number, totalS: number) => arriveAtMs - totalS * 1000;

/** The last arrival the server accepts: leave within `maxAheadH` (G-68), rounded down to the minute. */
export const latestArrival = (nowMs: number, totalS: number, maxAheadH: number) =>
  Math.floor((nowMs + maxAheadH * HOUR + totalS * 1000) / MINUTE) * MINUTE;

const STEP = 5 * MINUTE;

/**
 * The arrivals the dropdowns offer: the exact earliest minute, then every 5-minute mark up to the
 * latest arrival (Tim, 2026-10-13: dropdowns instead of a typed date field).
 */
export function arrivalSlots(earliestMs: number, latestMs: number): number[] {
  if (latestMs < earliestMs) return [];
  const slots = [earliestMs];
  for (let at = Math.ceil(earliestMs / STEP) * STEP; at <= latestMs; at += STEP)
    if (at > earliestMs) slots.push(at);
  return slots;
}

/** Local calendar day of a time, `YYYY-MM-DD`. */
export const dayKey = (ms: number) => toLocalInput(ms).slice(0, 10);

export function slotDays(slots: readonly number[], nowMs: number) {
  const today = dayKey(nowMs);
  const tomorrow = dayKey(
    new Date(new Date(nowMs).setDate(new Date(nowMs).getDate() + 1)).getTime()
  );
  const days: { key: string; label: string }[] = [];
  for (const slot of slots) {
    const key = dayKey(slot);
    if (days.some((d) => d.key === key)) continue;
    // Short: the day shares a phone-width row with hour and minute.
    const label =
      key === today
        ? 'Today'
        : key === tomorrow
          ? 'Tomorrow'
          : new Date(slot).toLocaleDateString([], { weekday: 'short', day: 'numeric' });
    days.push({ key, label });
  }
  return days;
}

export const slotHours = (slots: readonly number[], day: string) => [
  ...new Set(slots.filter((s) => dayKey(s) === day).map((s) => new Date(s).getHours())),
];

export const slotMinutes = (slots: readonly number[], day: string, hour: number) =>
  slots
    .filter((s) => dayKey(s) === day && new Date(s).getHours() === hour)
    .map((s) => new Date(s).getMinutes());

/** The slot for a day, hour and minute; keeps the hour or minute only while it still exists. */
export function pickSlot(
  slots: readonly number[],
  day: string,
  hour: number,
  minute: number
): number | null {
  const inDay = slots.filter((s) => dayKey(s) === day);
  const inHour = inDay.filter((s) => new Date(s).getHours() === hour);
  if (inHour.length === 0) return inDay[0] ?? null;
  return inHour.find((s) => new Date(s).getMinutes() === minute) ?? inHour[0] ?? null;
}

/** The nearest slot to any time, and which edge of the window it was moved from, if any. */
export function snapToSlot(
  atMs: number,
  slots: readonly number[]
): { at: number; adjusted: 'early' | 'late' | null } {
  const first = slots[0];
  const last = slots[slots.length - 1];
  if (first === undefined || last === undefined) return { at: atMs, adjusted: null };
  if (atMs < first) return { at: first, adjusted: 'early' };
  if (atMs > last) return { at: last, adjusted: 'late' };
  const nearest = slots.reduce((a, b) => (Math.abs(b - atMs) < Math.abs(a - atMs) ? b : a));
  return { at: nearest, adjusted: null };
}

/** Quick choices: the earliest arrival, then one and two hours from now (P32), within the window. */
export function quickArrivals(
  nowMs: number,
  earliestMs: number,
  latestMs: number = Number.POSITIVE_INFINITY
) {
  const hours = [1, 2].map((h) => {
    const at = Math.ceil((nowMs + h * HOUR) / STEP) * STEP;
    return { label: `+${h} h`, at, disabled: at < earliestMs || at > latestMs };
  });
  return [{ label: 'Earliest', at: earliestMs, disabled: false }, ...hours];
}

const pad = (n: number) => String(n).padStart(2, '0');

/** `YYYY-MM-DDTHH:mm` in local time, the value format of `<input type="datetime-local">`. */
export function toLocalInput(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Ships of `systemId` held back by open timed orders (G-67). */
export const reservedAt = (
  list: FleetOrderListResponse | undefined,
  systemId: string
): ShipCounts => list?.reserved.find((r) => r.systemId === systemId)?.ships ?? {};

export function subtractShips(a: ShipCounts, b: ShipCounts): ShipCounts {
  const out: ShipCounts = {};
  for (const [type, n] of Object.entries(a) as [ShipType, number][]) {
    const left = n - (b[type] ?? 0);
    if (left > 0) out[type] = left;
  }
  return out;
}

/** The selection leaves when its slowest class must: the earliest of the classes' latest departures. */
export function selectionDeparture(
  option: ReinforceOption,
  ships: readonly { shipType: ShipType; count: number }[]
): string | null {
  const times = ships.filter((s) => s.count > 0).map((s) => option.latestDeparture[s.shipType]);
  if (times.length === 0 || times.some((t) => t === undefined)) return null;
  return (times as string[]).reduce((a, b) => (a < b ? a : b));
}

export const reinforceLink = (
  galaxyId: string,
  attack: { targetSystemId: string; mission: ReinforceMission; arriveAt: string }
) =>
  `/g/${galaxyId}/fleet/reinforce?${new URLSearchParams({
    target: attack.targetSystemId,
    mission: attack.mission,
    arriveAt: attack.arriveAt,
  }).toString()}`;
